export const Messages = {
  Errors: {
    Tier: {
      Viewing: (tierName: string) =>
        `You need to have at least "${tierName}" tier to view this information.`,
      Acting: (tierName: string) =>
        `You need to have at least "${tierName}" tier to perform this action.`,
    },
    Permission: {
      Viewing: (roleName: string) =>
        `You need the permission "${roleName}" to view this information.`,
      Acting: (roleName: string) =>
        `You need the permission "${roleName}" to perform this action.`,
      ActingMultiple: (roleNames: string[]) =>
        `You need at least one of these permissions to perform this action: ${roleNames
          .map((name) => `"${name}"`)
          .join(' or ')}.`,
      Generic:
        'You are missing the required permission to perform this action.',
    },
    Network: {
      Generic: 'An unexpected network error occurred. Please try again.',
      Processing: (resource: string) =>
        `There was an issue processing ${resource}. Please refresh and try again.`,
      Loading: (resource: string) =>
        `There was an issue loading ${resource}. Please refresh and try again.`,
      Creating: (resource: string) =>
        `There was an issue creating ${resource}. Please try again.`,
      Updating: (resource: string) =>
        `There was an issue updating ${resource}. Please try again.`,
      Deleting: (resource: string) =>
        `There was an issue deleting ${resource}. Please try again.`,
      Removing: (resource: string) =>
        `There was an issue removing ${resource}. Please try again.`,
      Exporting: (resource: string) =>
        `There was an issue exporting ${resource}. Please try again.`,
      Running: (resource: string) =>
        `There was an issue running ${resource}. Please try again.`,
      Renaming: (resource: string) =>
        `There was an issue renaming ${resource}. Please try again.`,
      Copying: (resource: string) =>
        `There was an issue copying ${resource}. Please try again.`,
      Cloning: (resource: string) =>
        `There was an issue cloning ${resource}. Please try again.`,
      Searching: (resource: string) =>
        `There was an issue searching ${resource}. Please try again.`,
      Adding: (resource: string) =>
        `There was an issue adding ${resource}. Please try again.`,
      Emptying: (resource: string) =>
        `There was an issue emptying ${resource}. Please try again.`,
      Restoring: (resource: string) =>
        `There was an issue restoring ${resource}. Please try again.`,
      Saving: (resource: string) =>
        `There was an issue saving ${resource}. Please try again.`,
      Starting: (resource: string) =>
        `There was an issue starting ${resource}. Please try again.`,
      Pinning: (resource: string) =>
        `There was an issue pinning ${resource}. Please try again.`,
      Trashing: (resource: string) =>
        `There was an issue moving ${resource} to the trash. Please try again.`,
      Generating: (resource: string) =>
        `There was an issue generating ${resource}. Please refresh and try again.`,
    },
  },
  Success: {
    Created: (resource: string) => `${resource} created successfully.`,
    Updated: (resource: string) => `${resource} updated successfully.`,
    Deleted: (resource: string) => `${resource} deleted successfully.`,
    Removed: (resource: string) => `${resource} removed successfully.`,
    Exported: (resource: string) => `${resource} exported successfully.`,
    Renamed: (resource: string) => `${resource} renamed successfully.`,
    Renaming: (resource: string) =>
      `Renaming ${resource}. This may take a few minutes.`,
    Copied: (resource: string) => `${resource} copied successfully.`,
    Cloned: (resource: string) => `${resource} cloned successfully.`,
    Added: (resource: string) => `${resource} added successfully.`,
    Emptied: (resource: string) => `${resource} emptied successfully.`,
    Restored: (resource: string) => `${resource} restored successfully.`,
    Saved: (resource: string) => `${resource} saved successfully.`,
    Started: (resource: string) => `${resource} started successfully.`,
    Trashed: (resource: string) => `${resource} moved to trash successfully.`,
    Scheduled: (resource: string) => `${resource} scheduled successfully.`,
    Cancelled: (resource: string) => `${resource} cancelled successfully.`,
    Wiped: (resource: string) => `${resource} wiped successfully.`,
    Pinned: (resource: string) => `${resource} pinned successfully.`,
    Unpinned: (resource: string) => `${resource} unpinned successfully.`,
    Disconnected: (resource: string) =>
      `${resource} disconnected successfully.`,
  },
  Info: {
    AllUsersAlreadyAdded: (groupName?: string) =>
      groupName
        ? `All current and future users are already added to ${groupName}.`
        : 'All current and future users are already added to this group.',
    SecuritySettingsDisabled: (groupName?: string) =>
      groupName
        ? `This option is disabled for ${groupName} with security settings or security labels.`
        : 'This option is disabled for groups with security settings or security labels.',
    DriveStorageUnavailable:
      'Unable to pull Drive Storage information at this time.',
    NothingSelected:
      'You must select at least one item to perform this action.',
    GroupTemplateChangedSinceReview:
      'This template was updated since you selected it. Review the changes and apply again.',
    GroupSecuritySettingsNotLoaded:
      "This group's security settings are still loading. Please wait a moment and try again.",
  },
  UserName: {
    Invalid:
      'Only letters, numbers, periods, hyphens and underscores are allowed.',
    TooLong: (maxLength: number) =>
      `User Name cannot exceed ${maxLength} characters.`,
    Taken: 'User Name already exists.',
    JustTaken: 'This User Name is already in use. Please choose another.',
    Unverified: 'Could not verify User Name. Please try again.',
    Suggested: (takenUserName: string, suggestion: string) =>
      `${takenUserName} is taken — suggested ${suggestion}`,
    TakenNoSuggestion: (takenUserName: string) =>
      `${takenUserName} is taken. Please enter a different User Name.`,
    UseSuggestion: (suggestion: string) => `Use ${suggestion}`,
  },
  QuickReports: {
    ReportOpened: 'The generated report has opened in a new tab.',
    NoPermission: 'You do not have permission to run any quick reports.',
    NoPermissionSingle: 'You do not have permission to run this report.',
    OneTimeRunInfo:
      'This will execute immediately as a one-time run and will not be saved.',
    DateRangeHint:
      "Provide a valid date range for your report's data. It is defaulted to the last 7 days.",
    DaysBackHint:
      'Please provide the number of days back from today that you would like to report on.',
  },
  BulkOps: {
    NoCreatePermission:
      'You are missing the required permissions to be able to create bulk operations.',
    NoDeletePermission:
      'You do not have the required permission(s) to delete one or more of the bulk op types you have selected.',
  },
  DriveLabels: {
    RemoveSuccess: (all: boolean, count: number) =>
      all
        ? 'Successfully removed all labels from selected files.'
        : `Successfully removed selected label${count === 1 ? '' : 's'}.`,
    RemoveTask: (all: boolean, count: number) =>
      all
        ? 'The task to remove all labels has started.'
        : `The task to remove label${count === 1 ? '' : 's'} has started.`,
    NoWriteAccess:
      'gPanel needs access to manage Drive labels. Grant the Drive Labels scope in the Google Admin console.',
    DeleteInProgress: 'Deleting…',
    DeletePublishedHint: 'To delete this label, first disable it',
    DisableLockedHint:
      'This label is associated with rule(s) that lock it from changes',
    CannotDisableUnpublished: 'Only published labels can be disabled.',
    CannotEnableNonDisabled: 'Only disabled labels can be enabled.',
    DisableLockedSelectionHint:
      'One or more selected labels are associated with rule(s) that lock them from changes.',
    DisableTitle: (count: number) =>
      count === 1 ? 'Disable Label' : 'Disable Labels',
    DisableBodyBefore: 'People won’t be able to apply',
    DisableBodyAfter:
      'any longer, but it will still show up anywhere it’s already been applied and in search results.',
    DisableBodyMany:
      'People won’t be able to apply these labels any longer, but they will still show up anywhere they’ve already been applied and in search results.',
    DisableButton: 'Disable and Show',
    DisableSuccess: (count: number) =>
      count === 1 ? 'Label has been disabled.' : 'Labels have been disabled.',
    DisablePartial: (disabled: number, total: number, firstError: string) =>
      `Disabled ${disabled} of ${total} labels. ${firstError}`,
    EnableTitle: (count: number) =>
      count === 1 ? 'Enable Label' : 'Enable Labels',
    EnableBodyBefore: 'Enabling will publish and make',
    EnableBodyAfter: 'visible to users.',
    EnableBodyMany:
      'Enabling will publish and make these labels visible to users.',
    EnableSuccess: (count: number) =>
      count === 1 ? 'Label has been enabled.' : 'Labels have been enabled.',
    EnablePartial: (enabled: number, total: number, firstError: string) =>
      `Enabled ${enabled} of ${total} labels. ${firstError}`,
    PublishTitle: 'Publish Label',
    PublishWarningLead: 'Field types from',
    PublishWarningRest: 'can not be edited after publishing.',
    PublishInProgress: 'Publishing…',
    PublishSuccess: 'Label published.',
    PublishFailed: 'Failed to publish the label.',
    CannotDeletePublished:
      'You cannot delete published labels unless they are disabled.',
    DeleteTitle: (count: number) =>
      count === 1 ? 'Delete Label' : 'Delete Labels',
    DeleteManyButton: 'Delete Labels',
    // What follows the subject, which is the bold label name or "This label". The dialog builds the
    // sentence so the name can be bold.
    DeletePermanentRest:
      'will be removed from all items previously applied and removed from search shortly thereafter. This action cannot be undone.',
    DeleteDraftRest:
      'and its fields will be removed. This action cannot be undone.',
    DeleteEmptyDraftRest: 'will be removed. This action cannot be undone.',
    DeleteSuccess: (count: number) =>
      count === 1 ? 'Label has been deleted.' : 'Labels have been deleted.',
    DeletePartial: (deleted: number, total: number, firstError: string) =>
      `Deleted ${deleted} of ${total} labels. ${firstError}`,
  },
  BulkOperations: {
    TaskStarted: 'The task to run this bulk operation has begun.',
    MissingPermission:
      'You are missing the required permission to perform this action.',
    AlreadyRunning: 'A similar Bulk Operation is already in progress.',
  },
  ProfilePicture: {
    CreatedUserUploadFailed:
      'User created, but the profile picture could not be uploaded. You can add it from User Details.',
  },
  SharedDrives: {
    MembersTaskStarted:
      "We started a task to apply these member changes. You'll be notified when it's done.",
    CreatedMembersTaskStarted:
      "Shared drive has been created. We started a task to add the members you selected; you'll be notified when it's done.",
    CreatedWithMembers: 'Shared drive created and members added.',
    CreatingWithMembers:
      'Shared drive is currently being created and members have been added!',
    CreatedMembersFailed: (reason: string) =>
      `Shared drive created, but members could not be added (${reason}). Use Manage Members to add them.`,
  },
  CommandDashboard: {
    GreetingSubtitle:
      'Monitor your daily active usage and login frequency among existing gPanel customers.',
    EmptyStateMessage:
      "You haven't added any tiles to monitor and manage your Google Workspace environment.",
    LoadingDashboardView: 'Loading your latest dashboard view...',
    RecentAdminActionsTileLoadError:
      'There was an issue loading Recent Admin Actions tile data. Please refresh and try again.',
    RulesEngineTileLoadError:
      'There was an issue loading Rules Engine tile metrics. Please refresh and try again.',
    ActiveUsersTileLoadError:
      'There was an issue loading Active Users tile data. Please refresh and try again.',
    LoginActivityTileLoadError:
      'There was an issue loading Activity Logs tile metrics. Please refresh and try again.',
    LoginActivityTileRefreshError:
      'There was an issue refreshing Activity Logs tile metrics. Please try again.',
    LoginActivityTileRefreshStarted:
      'The task to refresh Activity Logs tile metrics has started. The tile will update when' +
      ' it finishes.',
    LoginActivityTileRefreshAlreadyRunning:
      'Activity Logs is already refreshing. The tile will update when that task finishes.',
    MasterSchedulerTileLoadError:
      'There was an issue loading Master Scheduler tile data. Please refresh and try again.',
    MasterSchedulerTileRefreshError:
      'There was an issue refreshing the Master Scheduler tile. Please try again.',
    MasterSchedulerTileRefreshStillProcessing:
      'The Master Scheduler refresh is taking longer than expected and is still processing.',
    GroupsAndAccessTileLoadError:
      'There was an issue loading Groups and Access tile metrics. Please refresh and try again.',
    GroupsAndAccessTileRefreshError:
      'There was an issue refreshing Groups and Access tile metrics. Please try again.',
    GroupsAndAccessTileRefreshStarted:
      'The task to refresh Groups and Access tile metrics has started. The tile will update when' +
      ' it finishes.',
    GroupsAndAccessTileRefreshAlreadyRunning:
      'Groups and Access is already refreshing. The tile will update when that task finishes.',
  },
} as const;
